/**
 * A session's two labels in the Sessions list (owner, 2026-10-07):
 *   1. what started it: the app (Web / Mac / Windows), or Calendar for a bot
 *      the calendar added;
 *   2. its mode: Bot Mode (a Recall bot in the meeting) or Audio Mode (a
 *      desktop call that captures the audio itself: the standalone mode).
 *
 * The app comes from `metadata.platform` on a desktop call ('macos' /
 * 'windows'), and from `metadata.clientPlatform` on a bot added from an app
 * (written since 2026-10-07; a bot session's `metadata.platform` is the
 * MEETING platform, never the app). An older bot session has no app: the
 * first label is left out.
 */

/** `metadata.source`. */
export const SESSION_SOURCE = {
  calendar: 'calendar',
  manual: 'manual',
  standaloneDesktop: 'standalone_desktop',
} as const;

/** The app a session ran in (backend `constants/clientPlatforms.js`; a desktop call's `metadata.platform`). */
export const CLIENT_PLATFORM = {
  web: 'web',
  macos: 'macos',
  windows: 'windows',
} as const;

export const SESSION_TYPE_COPY = {
  web: 'Web',
  mac: 'Mac',
  windows: 'Windows',
  calendar: 'Calendar',
  botMode: 'Bot Mode',
  audioMode: 'Audio Mode',
  unknown: 'Unknown',
} as const;

const APP_LABEL: Record<string, string> = {
  [CLIENT_PLATFORM.web]: SESSION_TYPE_COPY.web,
  [CLIENT_PLATFORM.macos]: SESSION_TYPE_COPY.mac,
  [CLIENT_PLATFORM.windows]: SESSION_TYPE_COPY.windows,
};

export interface SessionTypeLabel {
  /** First line: what started the session; absent when it is not known. */
  platformLabel?: string;
  /** Second line: the mode. */
  modeLabel: string;
}

export interface SessionTypeInput {
  source?: string | null;
  /** `metadata.platform`: the app on a desktop call, the meeting platform on a bot. */
  platform?: string | null;
  /** `metadata.clientPlatform`: the app a bot was added from. */
  clientPlatform?: string | null;
}

const appLabel = (value?: string | null): string | undefined => (value ? APP_LABEL[value] : undefined);

const withPlatform = (modeLabel: string, platformLabel?: string): SessionTypeLabel =>
  platformLabel ? { platformLabel, modeLabel } : { modeLabel };

export const getSessionType = ({ source, platform, clientPlatform }: SessionTypeInput = {}): SessionTypeLabel => {
  switch (source) {
    case SESSION_SOURCE.calendar:
      return withPlatform(SESSION_TYPE_COPY.botMode, SESSION_TYPE_COPY.calendar);
    case SESSION_SOURCE.manual:
      return withPlatform(SESSION_TYPE_COPY.botMode, appLabel(clientPlatform));
    case SESSION_SOURCE.standaloneDesktop:
      return withPlatform(SESSION_TYPE_COPY.audioMode, appLabel(platform));
    default:
      return { modeLabel: SESSION_TYPE_COPY.unknown };
  }
};

/** Both labels on one line ("Calendar · Bot Mode"), for a compact cell. */
export const sessionTypeText = (label: SessionTypeLabel): string =>
  [label.platformLabel, label.modeLabel].filter(Boolean).join(' · ');
